import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import { getTimezoneDisplayName, getUserTimezone } from '../utils/dateUtils';
import {
  formatEmploymentPercent,
  percentToFactorString,
  validateEmploymentPercentInput
} from '../utils/employmentScale';
import type { RuleRow } from './workingCalendar/workingCalendarDualViewShared';
import MemberMyWorkCalendarDialog from './workingCalendar/MemberMyWorkCalendarDialog';

export const WORKING_RULE_TYPES = {
  WEEKDAY_HOURS: 'WEEKDAY_HOURS',
  ANNUAL_OFF: 'ANNUAL_OFF',
  DATE_OFF: 'DATE_OFF',
  DATE_HOURS: 'DATE_HOURS'
} as const;

type CalSummary = { id: string; name: string; archived: boolean };

type Props = {
  organizationId: string;
  userId: string;
  /** When false, assignment / employment / bulk are hidden; personal rules still editable. */
  allowOrgAdminEdits: boolean;
};

const UserWorkingCalendarPanel: React.FC<Props> = ({ organizationId, userId, allowOrgAdminEdits }) => {
  const { formatDateTime, fromDateTimeLocal, preferences } = useDateFormat();
  const userTz = getUserTimezone(preferences);
  const userTzLabel = getTimezoneDisplayName(userTz);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [calendars, setCalendars] = useState<CalSummary[]>([]);
  const [assignments, setAssignments] = useState<
    Array<{
      id: string;
      workingCalendarId: string;
      effectiveFrom: string;
      workingCalendar: { id: string; name: string; archived: boolean };
    }>
  >([]);
  const [employment, setEmployment] = useState<Array<{ id: string; effectiveFrom: string; factor: string }>>([]);
  const [orgRulesFrozen, setOrgRulesFrozen] = useState<RuleRow[]>([]);
  const [myCalendarOpen, setMyCalendarOpen] = useState(false);

  const [assignCalId, setAssignCalId] = useState<string | null>(null);
  const [assignFrom, setAssignFrom] = useState('');
  const [empFrom, setEmpFrom] = useState('');
  const [empPercent, setEmpPercent] = useState('100');


  const pickableCalendars = useMemo(() => calendars.filter((c) => !c.archived), [calendars]);

  const load = useCallback(async () => {
    if (!organizationId || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const [listRes, asRes, emRes] = await Promise.all([
        api.listWorkingCalendars(organizationId, true),
        api.listMemberWorkingCalendarAssignments(organizationId, userId),
        api.listMemberEmploymentScales(organizationId, userId)
      ]);

      const list = (listRes?.data ?? listRes) as CalSummary[];
      setCalendars(Array.isArray(list) ? list : []);

      const rows = (asRes?.data ?? asRes) as typeof assignments;
      setAssignments(Array.isArray(rows) ? rows : []);

      const em = (emRes?.data ?? emRes) as typeof employment;
      setEmployment(Array.isArray(em) ? em : []);

      const latest = Array.isArray(rows) && rows.length ? rows[0] : null;
      if (latest?.workingCalendarId) {
        const detail = await api.getWorkingCalendar(organizationId, latest.workingCalendarId);
        const d = detail?.data ?? detail;
        const rules = (d?.rules as Array<{ sortOrder: number; ruleType: string; payload: unknown }>) || [];
        setOrgRulesFrozen(
          rules.map((r) => ({
            sortOrder: r.sortOrder,
            ruleType: r.ruleType,
            payload: (r.payload as Record<string, unknown>) || {}
          }))
        );
      } else {
        setOrgRulesFrozen([]);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to load working calendar data');
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const addAssignment = async () => {
    if (!allowOrgAdminEdits || !assignCalId || !assignFrom.trim()) {
      setError('Choose a calendar and effective from (required).');
      return;
    }
    setError(null);
    try {
      await api.createMemberWorkingCalendarAssignment(organizationId, userId, {
        workingCalendarId: assignCalId,
        effectiveFrom: fromDateTimeLocal(assignFrom)
      });
      setAssignCalId(null);
      setAssignFrom('');
      setSuccess('Assignment saved.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to add assignment');
    }
  };

  const removeAssignment = async (id: string) => {
    if (!allowOrgAdminEdits) return;
    if (!window.confirm('Delete this assignment row?')) return;
    try {
      await api.deleteMemberWorkingCalendarAssignment(organizationId, userId, id);
      setSuccess('Assignment removed.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const addEmployment = async () => {
    if (!allowOrgAdminEdits || !empFrom.trim()) return;
    const percentErr = validateEmploymentPercentInput(empPercent);
    if (percentErr) {
      setError(percentErr);
      return;
    }
    const factor = percentToFactorString(empPercent);
    if (!factor) {
      setError('Employment % must be between 0 and 100.');
      return;
    }
    setError(null);
    try {
      await api.createMemberEmploymentScale(organizationId, userId, {
        effectiveFrom: fromDateTimeLocal(empFrom),
        factor
      });
      setEmpFrom('');
      setEmpPercent('100');
      setSuccess('Employment row saved.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to save employment scale');
    }
  };

  const removeEmployment = async (id: string) => {
    if (!allowOrgAdminEdits) return;
    if (!window.confirm('Delete this employment scale row?')) return;
    try {
      await api.deleteMemberEmploymentScale(organizationId, userId, id);
      setSuccess('Row removed.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  if (loading) return <LinearProgress />;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Dates and times use your User preference timezone (<strong>{userTzLabel}</strong>) before they are stored as UTC.
      </Typography>

      <Typography variant="subtitle1" gutterBottom>
        Assigned org calendar
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Org base rules are read-only here. Managers add effective-dated assignment rows (no default pre-filled effective date).
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Calendar</TableCell>
              <TableCell>Effective from</TableCell>
              {allowOrgAdminEdits && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.workingCalendar?.name || a.workingCalendarId}</TableCell>
                <TableCell>{formatDateTime(a.effectiveFrom)}</TableCell>
                {allowOrgAdminEdits && (
                  <TableCell align="right">
                    <Tooltip title="Delete row">
                      <IconButton size="small" onClick={() => removeAssignment(a.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {assignments.length === 0 && (
              <TableRow>
                <TableCell colSpan={allowOrgAdminEdits ? 3 : 2}>
                  <Typography variant="body2" color="text.secondary">
                    No assignment rows.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {allowOrgAdminEdits && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 3 }}>
          <Autocomplete
            sx={{ minWidth: 220 }}
            options={pickableCalendars}
            getOptionLabel={(o) => o.name}
            value={pickableCalendars.find((c) => c.id === assignCalId) || null}
            onChange={(_, v) => setAssignCalId(v?.id || null)}
            renderInput={(params) => <TextField {...params} label="Calendar" size="small" />}
          />
          <TextField
            label="Effective from"
            type="datetime-local"
            size="small"
            value={assignFrom}
            onChange={(e) => setAssignFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 240 }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={addAssignment}>
            Add assignment
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" gutterBottom>
        Employment (FTE %)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Scales nominal hours on working days (e.g. 50% → half of org calendar hours). 0% is allowed.
      </Typography>
      {allowOrgAdminEdits && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <TextField
            label="Effective from"
            type="datetime-local"
            size="small"
            value={empFrom}
            onChange={(e) => setEmpFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Employment %"
            type="number"
            size="small"
            value={empPercent}
            onChange={(e) => setEmpPercent(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 'any' }}
            helperText="0–100"
            sx={{ width: 140 }}
          />
          <Button variant="outlined" onClick={addEmployment}>
            Add row
          </Button>
        </Box>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Effective from</TableCell>
              <TableCell>Employment %</TableCell>
              {allowOrgAdminEdits && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {employment.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{formatDateTime(r.effectiveFrom)}</TableCell>
                <TableCell>{formatEmploymentPercent(r.factor)}</TableCell>
                {allowOrgAdminEdits && (
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => removeEmployment(r.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {employment.length === 0 && (
              <TableRow>
                <TableCell colSpan={allowOrgAdminEdits ? 3 : 2}>
                  <Typography variant="body2" color="text.secondary">
                    No rows — treated as 100% for resolution.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" gutterBottom>
        My work calendar
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add your own rules on top of the organization calendar and employment %. Use the same dual-view editor
        as organization calendars; organization rules are frozen.
      </Typography>
      <Button variant="contained" startIcon={<EditIcon />} onClick={() => setMyCalendarOpen(true)}>
        Edit my work calendar
      </Button>

      <MemberMyWorkCalendarDialog
        open={myCalendarOpen}
        onClose={() => setMyCalendarOpen(false)}
        organizationId={organizationId}
        userId={userId}
        orgRulesFrozen={orgRulesFrozen}
        onSaved={() => {
          setSuccess('My work calendar saved.');
          setMyCalendarOpen(false);
          load();
        }}
      />
    </Box>
  );
};

export default UserWorkingCalendarPanel;
